import React from "react";
import { useNavigate } from "react-router";
import useMediaQuery, { COMPACT_SHELL_QUERY } from "../hooks/useMediaQuery.js";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { consolidationRequest } from "../api/consolidationApi.js";
import { dispatchResultsShellNavigation } from "../utils/resultsDetailViewNavigation.js";
import {
  DEFAULT_RIGHT_PANEL_COST_LABEL,
  buildResultsRightPanelData,
  formatRightPanelDayLabel,
  getFleetRowDisplayedMinutesSnapshot,
  getResultsRightPanelSnapshot,
  resolveRightPanelTruckRows,
  subscribeFleetRowDisplayedMinutes,
  subscribeResultsRightPanel,
} from "../utils/rightPanelData.js";
import { HoverCard, joinClasses } from "./ResultsPrimitives.jsx";

const WATCH_STRIPE_CLASS_NAMES = {
  amber: "bg-[#d08b3e]",
  blue: "bg-[#3b5bab]",
  neutral: "bg-slate-400",
  red: "bg-[var(--dispatch-update-fill,#9c3b31)]",
};

const DAY_STATUS_CLASS_NAMES = {
  amber: "border-[rgba(208,139,62,0.3)] bg-[rgba(250,236,211,0.95)] text-[rgb(146_97_24)]",
  blue: "border-[#c7d4f3] bg-[#e8eefb] text-[#1e3a8a]",
  neutral: "border-slate-200 bg-slate-50 text-slate-600",
};

const CARD_CLASS_NAME = "rounded-[4px] border border-[var(--shell-chrome-line)] bg-white";
const ROW_BUTTON_CLASS_NAME = "w-full text-left transition-colors duration-150 hover:bg-slate-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent";
const NUMBER_FORMAT = new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 1 });
const CURRENCY_FORMAT = new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });

function formatHours(minutes) {
  const rounded = Math.max(Math.round(Number(minutes) || 0), 0);
  const hours = Math.floor(rounded / 60);
  const remaining = rounded % 60;
  return remaining ? `${hours} h ${String(remaining).padStart(2, "0")}` : `${hours} h`;
}

function formatRemaining(minutes) {
  const rounded = Math.round(Number(minutes) || 0);
  if (rounded <= 0) {
    return "0 min";
  }
  return rounded < 60 ? `${rounded} min` : formatHours(rounded);
}

function formatTons(value) {
  return `${NUMBER_FORMAT.format(Number(value) || 0)} t`;
}

function navigateInDispatch(detail) {
  dispatchResultsShellNavigation({ historyMode: "replace", scroll: true, syncLocation: true, ...detail });
}

function SectionTitle({ children, trailing = null }) {
  return (
    <div className="mb-1.5 flex items-center justify-between gap-2">
      <div className="inline-flex items-center gap-2">
        <span className="h-[5px] w-[5px] rounded-none bg-accent" aria-hidden="true" />
        <h3 className="text-[9.5px] font-black uppercase tracking-[0.14em] text-accent-strong">{children}</h3>
      </div>
      {trailing}
    </div>
  );
}

function HoverLines({ lines = [], note = "" }) {
  return (
    <div className="grid min-w-60 gap-1 text-[12px] leading-[1.45] text-content-ink">
      {lines.map((line, index) => <p key={`${line}-${index}`} className="m-0">{line}</p>)}
      {note ? <p className="m-0 border-t-[0.5px] border-[rgba(0,0,0,0.08)] pt-1.5 text-content-muted">{note}</p> : null}
    </div>
  );
}

function EstimatedCostHoverCardContent({ details = {} }) {
  const lines = Array.isArray(details?.lines) ? details.lines : [];
  const items = Array.isArray(details?.items) ? details.items : [];

  return (
    <div className="flex min-w-80 flex-col gap-2">
      {lines.length > 0 ? (
        <div className="grid gap-1.5">
          {lines.map((line) => (
            <div key={line.label} className="grid grid-cols-[104px_1fr] items-center gap-2 text-[12px]">
              <span className="truncate font-semibold text-content-ink">{line.label}</span>
              <span className="text-content-muted">{line.value}</span>
            </div>
          ))}
        </div>
      ) : null}
      {items.length > 0 ? (
        <div className="grid max-h-66 gap-1.5 overflow-y-auto pr-1">
          {items.map((item) => (
            <div key={item.key} className="grid gap-0.5 border-b border-[rgba(0,0,0,0.06)] pb-1.5 last:border-b-0 last:pb-0">
              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-2 text-[12px]">
                <span className="font-semibold leading-[1.35] text-content-ink">{item.label}</span>
                <span className="shrink-0 text-right font-semibold leading-[1.35] text-content-ink">{item.value}</span>
              </div>
              {item.meta ? <span className="text-[10px] leading-[1.45] text-content-muted">{item.meta}</span> : null}
            </div>
          ))}
        </div>
      ) : null}
      {details?.note ? <p className="m-0 border-t-[0.5px] border-[rgba(0,0,0,0.08)] pt-2 text-[12px] leading-[1.55] text-[#374151]">{details.note}</p> : null}
      {details?.total ? (
        <div className="flex items-center justify-between border-t-[0.5px] border-[rgba(0,0,0,0.08)] pt-2">
          <span className="text-[11px] text-content-muted">{details.totalLabel || "Total"}</span>
          <span className="text-[14px] font-bold text-content-ink">= {details.total}</span>
        </div>
      ) : null}
    </div>
  );
}

function WithHoverCard({ children, content = null, title = "" }) {
  if (!content) {
    return children;
  }

  return (
    <HoverCard title={title} content={content} placement="left-start" delay={120} maxWidth={420}>
      {children}
    </HoverCard>
  );
}

function WatchRow({ item }) {
  const RowElement = item.onSelect ? "button" : "div";
  const row = (
    <RowElement
      {...(item.onSelect ? { type: "button", onClick: item.onSelect } : {})}
      aria-label={[item.label, item.note].filter(Boolean).join(" · ")}
      className={joinClasses([
        item.onSelect ? ROW_BUTTON_CLASS_NAME : "w-full",
        "grid grid-cols-[3px_minmax(0,1fr)_auto_auto] items-center gap-1.75 border-b border-[var(--shell-chrome-line)] py-1.25 pr-1.5 last:border-b-0",
      ])}
    >
      <span className={joinClasses(["self-stretch", WATCH_STRIPE_CLASS_NAMES[item.tone] || WATCH_STRIPE_CLASS_NAMES.neutral])} aria-hidden="true" />
      <span className="min-w-0">
        <span className="block truncate text-[11px] font-bold leading-3.5 text-content-ink">{item.label}</span>
        {item.note ? (
          <span className={joinClasses([
            "block truncate text-[9.5px] leading-3",
            item.tone === "red" ? "font-bold text-[var(--dispatch-update-fill,#9c3b31)]" : "font-medium text-content-muted",
          ])}>
            {item.note}
          </span>
        ) : null}
      </span>
      <span className="whitespace-nowrap text-[10.5px] font-bold text-content-ink">{item.value || ""}</span>
      {item.onSelect ? <ChevronRightIcon className="h-3 w-3 text-[rgba(103,94,79,0.56)]" aria-hidden="true" /> : <span aria-hidden="true" />}
    </RowElement>
  );

  return <WithHoverCard content={item.hoverContent} title={item.label}>{row}</WithHoverCard>;
}

function buildWatchItems(day, truckRows, alertCount) {
  const items = [];

  if (alertCount > 0) {
    items.push({
      key: "alerts",
      label: `${alertCount} alerte${alertCount > 1 ? "s" : ""}`,
      note: "Blocages à corriger",
      onSelect: () => navigateInDispatch({ viewName: "errors" }),
      tone: "red",
    });
  }

  truckRows.usedRows.filter((row) => row.isNearLimit).forEach((row) => {
    items.push({
      key: `limit-${row.id}`,
      label: row.minutes > row.maxMinutes ? `${row.name} dépasse sa limite` : `${row.name} près de la limite`,
      note: `${formatHours(row.minutes)} / ${formatHours(row.maxMinutes)} · ${row.minutes > row.maxMinutes ? `${formatRemaining(row.minutes - row.maxMinutes)} de trop` : `reste ${formatRemaining(row.maxMinutes - row.minutes)}`}`,
      onSelect: () => navigateInDispatch({ truckId: row.id, viewName: "fleet" }),
      tone: "red",
    });
  });

  if (day.suspendedCount > 0) {
    items.push({
      key: "suspended",
      label: `${day.suspendedCount} suspendue${day.suspendedCount > 1 ? "s" : ""}`,
      note: "Non placées dans le Gantt",
      onSelect: () => navigateInDispatch({ viewName: "suspended" }),
      tone: "amber",
      value: formatTons(day.suspendedTons),
    });
  }

  if (day.canOptimize && (day.unprotectedOrderCount > 0 || day.excludedTrucks.length > 0)) {
    items.push({
      hoverContent: (
        <HoverLines
          lines={day.excludedTrucks.map((truck) => `${truck.label} : ${truck.reason}`)}
          note={`${day.unprotectedOrderCount} commande${day.unprotectedOrderCount > 1 ? "s non protégées" : " non protégée"} sur ${day.protectedOrderTotal} ${day.unprotectedOrderCount > 1 ? "peuvent être déplacées" : "peut être déplacée"}.`}
        />
      ),
      key: "optimize",
      label: "Au prochain Optimiser",
      note: `${day.unprotectedOrderCount} cmd déplaçable${day.unprotectedOrderCount > 1 ? "s" : ""} · ${day.excludedTrucks.length} camion${day.excludedTrucks.length > 1 ? "s" : ""} exclu${day.excludedTrucks.length > 1 ? "s" : ""}`,
      tone: "amber",
    });
  }

  if (day.staleNotice) {
    const causeCount = day.staleNotice.causeLines.length;
    items.push({
      hoverContent: causeCount > 0
        ? (
          <HoverLines
            lines={day.staleNotice.causeLines.slice(0, 8)}
            note={causeCount > 8 ? `… et ${causeCount - 8} autres changements.` : ""}
          />
        )
        : null,
      key: "stale",
      label: causeCount > 0 ? `À revalider · ${causeCount} changement${causeCount > 1 ? "s" : ""}` : "À revalider",
      note: day.staleNotice.updatedAtLabel ? `depuis le ${day.staleNotice.updatedAtLabel.replace(/\s\d{4}/, "")}` : "",
      tone: "amber",
    });
  }

  day.trucks.filter((truck) => truck.hasEndOfDayLoad).forEach((truck) => {
    items.push({
      key: `pm-${truck.id}`,
      label: `Chargé PM · ${truck.name}`,
      note: truck.endOfDayTargetDate ? `Charge ce soir pour le ${formatRightPanelDayLabel(truck.endOfDayTargetDate)}` : "Charge ce soir",
      onSelect: () => navigateInDispatch({ truckId: truck.id, viewName: "fleet" }),
      tone: "blue",
    });
  });

  truckRows.freeTrucks.forEach((truck) => {
    items.push({
      key: `free-${truck.id}`,
      label: `${truck.name} disponible`,
      note: `${formatTons(truck.capacityTons)} · aucune étape planifiée`,
      onSelect: () => navigateInDispatch({ truckId: truck.id, viewName: "fleet" }),
      tone: "neutral",
    });
  });

  return items;
}

function Tile({ children = null, label, note = "", value }) {
  return (
    <div className={joinClasses([CARD_CLASS_NAME, "min-w-0 px-2 py-1.5"])}>
      <p className="m-0 whitespace-nowrap text-[8.5px] font-black uppercase tracking-[0.08em] text-accent-strong">{label}</p>
      <p className="m-0 mt-0.5 whitespace-nowrap text-[15px] font-extrabold leading-4.5 text-content-ink">{value}</p>
      {note ? <p className="m-0 truncate text-[9.5px] font-medium leading-3 text-content-muted">{note}</p> : null}
      {children}
    </div>
  );
}

function DaySection({ day, truckRows }) {
  const usedCount = truckRows.usedRows.length;
  const totalCount = day.trucks.length;
  const distanceKm = Math.round(day.distanceKm);
  const emptyPct = day.distanceKm > 0 ? Math.round((day.emptyDistanceKm / day.distanceKm) * 100) : 0;
  const costTile = (
    <Tile
      label={DEFAULT_RIGHT_PANEL_COST_LABEL}
      note={day.plannedTons > 0 && day.totalEstimatedCost ? `${NUMBER_FORMAT.format(Math.round((day.totalEstimatedCost / day.plannedTons) * 100) / 100)} $ / tonne` : ""}
      value={day.totalEstimatedCost ? CURRENCY_FORMAT.format(day.totalEstimatedCost) : "—"}
    />
  );

  return (
    <section>
      <SectionTitle>Journée</SectionTitle>
      <div className="grid grid-cols-2 gap-1.5">
        <div className={joinClasses([CARD_CLASS_NAME, "col-span-2 min-w-0 px-2 py-1.5"])}>
          <div className="flex items-baseline justify-between gap-2">
            <p className="m-0 text-[8.5px] font-black uppercase tracking-[0.08em] text-accent-strong">Planifié</p>
            <p className="m-0 text-[9.5px] text-content-muted">{day.orderCount} commandes</p>
          </div>
          <p className="m-0 mt-0.5 text-[15px] font-extrabold leading-4.5 text-content-ink">{formatTons(day.plannedTons)}</p>
          {day.products.length ? (
            <>
              <div className="mt-1 flex h-1 gap-px" aria-hidden="true">
                {day.products.map((product) => (
                  <span key={product.label} className="h-full" style={{ backgroundColor: product.accentColor, flexGrow: product.tons }} />
                ))}
              </div>
              <div className="mt-0.75 flex flex-wrap gap-x-2 gap-y-0.5">
                {day.products.map((product) => (
                  <span key={product.label} className="inline-flex items-center gap-0.75 whitespace-nowrap text-[9px] text-content-muted">
                    <span className="h-1.5 w-1.5 rounded-none" style={{ backgroundColor: product.accentColor }} aria-hidden="true" />
                    {product.label} {NUMBER_FORMAT.format(product.tons)}
                  </span>
                ))}
              </div>
            </>
          ) : null}
        </div>
        <Tile
          label="Camions"
          note={`${truckRows.freeTrucks.length} libre${truckRows.freeTrucks.length > 1 ? "s" : ""} · ${truckRows.disabledTrucks.length} hors`}
          value={`${usedCount} / ${totalCount}`}
        />
        <Tile
          label="Heures"
          note={day.firstStartLabel && day.lastEndLabel ? `${day.firstStartLabel} → ${day.lastEndLabel}` : ""}
          value={formatHours(truckRows.totalMinutes)}
        />
        <Tile
          label="Distance"
          note={distanceKm > 0 ? `dont ${emptyPct} % à vide` : ""}
          value={`${NUMBER_FORMAT.format(distanceKm)} km`}
        />
        {day.costDetails ? (
          <WithHoverCard content={<EstimatedCostHoverCardContent details={day.costDetails} />} title={DEFAULT_RIGHT_PANEL_COST_LABEL}>
            {costTile}
          </WithHoverCard>
        ) : costTile}
      </div>
    </section>
  );
}

function TruckRow({ row, thresholdPct }) {
  const barClassName = row.pct >= thresholdPct
    ? "bg-[var(--dispatch-update-fill,#9c3b31)]"
    : row.pct >= 75 ? "bg-[#d08b3e]" : "bg-[#4b5a55]";
  const loadPct = row.capacityTons > 0 ? Math.min((row.peakTons / row.capacityTons) * 100, 100) : 0;

  return (
    <WithHoverCard
      title={row.name}
      content={(
        <HoverLines lines={[
          `Temps : ${formatHours(row.minutes)} sur ${formatHours(row.maxMinutes)} (${Math.round(row.pct)} %)`,
          `Charge au pic : ${formatTons(row.peakTons)} sur ${formatTons(row.capacityTons)}`,
          `Compartiments : ${row.compartmentsUsed} / ${row.compartmentsTotal}`,
          `Distance : ${NUMBER_FORMAT.format(Math.round(row.distanceKm))} km`,
        ]} />
      )}
    >
      <button
        type="button"
        onClick={() => navigateInDispatch({ truckId: row.id, viewName: "fleet" })}
        aria-label={`${row.name} · ${formatHours(row.minutes)} sur ${formatHours(row.maxMinutes)}`}
        className={joinClasses([ROW_BUTTON_CLASS_NAME, "block border-b border-[var(--shell-chrome-line)] px-2 py-1"])}
      >
        <span className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-baseline gap-1.5">
          <span className="truncate text-[10.5px] font-bold text-content-ink">{row.name}</span>
          <span className="whitespace-nowrap text-[9px] text-content-muted">{NUMBER_FORMAT.format(row.peakTons)}/{NUMBER_FORMAT.format(row.capacityTons)} t</span>
          <span className="whitespace-nowrap text-[9.5px] text-content-muted">
            <b className={joinClasses(["text-[10.5px]", row.isNearLimit ? "text-[var(--dispatch-update-fill,#9c3b31)]" : "text-content-ink"])}>{formatHours(row.minutes)}</b>
            {" / "}{formatHours(row.maxMinutes)}
          </span>
        </span>
        <span className="mt-0.75 block h-1.25 bg-[#e9ebe7]" aria-hidden="true">
          <span className={joinClasses(["block h-full", barClassName])} style={{ width: `${Math.min(row.pct, 100)}%` }} />
        </span>
        <span className="mt-px block h-0.5 bg-[#e9ebe7]" aria-hidden="true">
          <span className="block h-full bg-[#b79b53]" style={{ width: `${loadPct}%` }} />
        </span>
      </button>
    </WithHoverCard>
  );
}

function DayRow({ label, note = "", noteBelow = false, onSelect = null, status }) {
  const content = (
    <>
      <span className="min-w-0">
        <span className="block whitespace-nowrap text-[10.5px] font-bold text-content-ink">{label}</span>
        {note && !noteBelow ? <span className="block truncate text-[9.5px] text-content-muted">{note}</span> : null}
      </span>
      <span className={joinClasses([
        "whitespace-nowrap rounded-[4px] border px-1.25 py-px text-[8.5px] font-extrabold uppercase tracking-[0.04em]",
        DAY_STATUS_CLASS_NAMES[status.tone] || DAY_STATUS_CLASS_NAMES.neutral,
      ])}>
        {status.label}
      </span>
      {/* Note sous l'étiquette : sur toute la largeur, sans être coupée par une étiquette longue. */}
      {note && noteBelow ? <span className="col-span-2 block text-[9.5px] text-content-muted">{note}</span> : null}
    </>
  );
  const className = "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-1.5 border-b border-[var(--shell-chrome-line)] px-2 py-1";

  return onSelect
    ? <button type="button" onClick={onSelect} className={joinClasses([ROW_BUTTON_CLASS_NAME, className])}>{content}</button>
    : <div className={className}>{content}</div>;
}

function useConsolidationPendingDays(isActive) {
  const today = new Date();
  const monthKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [result, setResult] = React.useState({ ready: null, started: null, monthKey: "" });

  React.useEffect(() => {
    if (!isActive || result.monthKey === monthKey) {
      return undefined;
    }

    // Requête lente (≈3 s) : chargée une seule fois, à l'ouverture de l'onglet Jours.
    const controller = new AbortController();
    consolidationRequest(`/calendar?${new URLSearchParams({ month: monthKey })}`, { signal: controller.signal })
      .then((payload) => {
        const days = Array.isArray(payload?.days) ? payload.days : [];
        // Journées closes, comptées avec les mots de la légende du calendrier : « Prêt à consolider » et « En cours ».
        const closed = (status) => days.filter((day) => day?.status === status && day?.dispatch_state === "closed").length;
        setResult({ ready: closed("pending"), started: closed("in_progress"), monthKey });
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setResult({ ready: -1, started: -1, monthKey });
        }
      });

    return () => controller.abort();
  }, [isActive, monthKey, result.monthKey]);

  const loaded = result.monthKey === monthKey;
  return {
    ready: loaded ? result.ready : null,
    started: loaded ? result.started : null,
    monthLabel: new Intl.DateTimeFormat("fr-CA", { month: "long" }).format(today),
  };
}

const monthDaysLabel = (count, monthLabel) => `${count} jour${count > 1 ? "s" : ""} ${/^[aeiouyâàéèêîïôû]/i.test(monthLabel || "") ? "d'" : "de "}${monthLabel}`;

const DETAIL_TABS = [
  { key: "trucks", label: "Camions" },
  { key: "mills", label: "Meuneries" },
  { key: "days", label: "Jours" },
];

function DetailTabs({ day, truckRows }) {
  const [activeTab, setActiveTab] = React.useState("trucks");
  const navigate = useNavigate();
  const consolidation = useConsolidationPendingDays(activeTab === "days");
  const counts = { mills: day.mills.length, trucks: truckRows.usedRows.length };

  return (
    <section>
      <div className="mb-1.5 grid grid-cols-3 overflow-hidden rounded-[4px] border border-slate-300" role="tablist">
        {DETAIL_TABS.map((tab) => {
          const isActive = tab.key === activeTab;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveTab(tab.key)}
              className={joinClasses([
                "py-1 text-[10px] font-extrabold transition-colors duration-150 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent",
                isActive ? "bg-accent text-white" : "bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900",
              ])}
            >
              {tab.label}{counts[tab.key] != null ? ` ${counts[tab.key]}` : ""}
            </button>
          );
        })}
      </div>

      <div className={joinClasses([CARD_CLASS_NAME, "overflow-hidden [&>*:last-child]:border-b-0"])}>
        {activeTab === "trucks" ? (
          <>
            {truckRows.usedRows.map((row) => <TruckRow key={row.id} row={row} thresholdPct={day.timeLimitThresholdPct} />)}
            {truckRows.disabledTrucks.length ? (
              <p className="m-0 px-2 py-1.25 text-[9.5px] leading-3.25 text-content-muted">
                Hors dispatch : {truckRows.disabledTrucks.map((truck) => truck.name).join(" · ")}
              </p>
            ) : null}
            {!truckRows.usedRows.length && !truckRows.disabledTrucks.length ? <p className="m-0 px-2 py-2 text-[10.5px] text-content-muted">Aucun camion planifié</p> : null}
          </>
        ) : null}

        {activeTab === "mills" ? (
          day.mills.length ? day.mills.map((mill) => (
            <div key={mill.name} className="border-b border-[var(--shell-chrome-line)] px-2 py-1">
              <div className="flex justify-between gap-1.5">
                <span className="truncate text-[10.5px] font-bold text-content-ink">{mill.name}</span>
                <b className="whitespace-nowrap text-[10.5px] text-content-ink">{formatTons(mill.tons)}</b>
              </div>
              <p className="m-0 text-[9.5px] text-content-muted">
                {mill.truckCount} camion{mill.truckCount > 1 ? "s" : ""}
                {mill.firstArrival ? ` · ${mill.firstArrival.replace(":", " h ")}` : ""}
                {mill.lastArrival && mill.lastArrival !== mill.firstArrival ? ` → ${mill.lastArrival.replace(":", " h ")}` : ""}
              </p>
            </div>
          )) : <p className="m-0 px-2 py-2 text-[10.5px] text-content-muted">Aucun chargement planifié</p>
        ) : null}

        {activeTab === "days" ? (
          <>
            {day.days.rows.map((row) => (
              <DayRow
                key={`${row.label}-${row.dispatchDate}`}
                label={row.label}
                note={row.note}
                onSelect={row.dispatchDate !== day.dispatchDate ? () => navigateInDispatch({ dispatchDate: row.dispatchDate, viewName: "fleet" }) : null}
                status={row.status}
              />
            ))}
            {consolidation.ready == null ? (
              <DayRow label="Consolidation" note="chargement…" noteBelow status={{ label: "Prêt à consolider", tone: "blue" }} />
            ) : [
              { count: consolidation.ready, status: { label: "Prêt à consolider", tone: "blue" } },
              { count: consolidation.started, status: { label: "En cours", tone: "amber" } },
            ].filter((row) => row.count > 0).map((row) => (
              <DayRow
                key={row.status.label}
                label={monthDaysLabel(row.count, consolidation.monthLabel)}
                note={`clôturé${row.count > 1 ? "s" : ""}, relevé à valider`}
                noteBelow
                onSelect={() => navigate("/consolidation")}
                status={row.status}
              />
            ))}
            {!day.days.rows.length && consolidation.ready != null && !(consolidation.ready > 0) && !(consolidation.started > 0) ? <p className="m-0 px-2 py-2 text-[10.5px] text-content-muted">Aucune journée à traiter</p> : null}
          </>
        ) : null}
      </div>
    </section>
  );
}

function RightPanel({
  collapsed = false,
  collapseToggle = null,
  data = null,
}) {
  const compact = useMediaQuery(COMPACT_SHELL_QUERY);
  const PanelBody = compact ? "details" : "div";
  const [bootstrappedPanelData] = React.useState(() => {
    const scriptElement = typeof document === "undefined" ? null : document.getElementById("results-data");
    if (!(scriptElement instanceof HTMLScriptElement)) {
      return null;
    }

    try {
      return buildResultsRightPanelData(JSON.parse(scriptElement.textContent || "null"), data);
    } catch {
      return null;
    }
  });
  const subscribedPanelData = React.useSyncExternalStore(
    subscribeResultsRightPanel,
    getResultsRightPanelSnapshot,
    getResultsRightPanelSnapshot,
  );
  const displayedMinutesByKey = React.useSyncExternalStore(
    subscribeFleetRowDisplayedMinutes,
    getFleetRowDisplayedMinutesSnapshot,
    getFleetRowDisplayedMinutesSnapshot,
  );
  const panelData = subscribedPanelData || bootstrappedPanelData;
  const day = panelData?.day || null;
  const truckRows = React.useMemo(() => resolveRightPanelTruckRows(day, displayedMinutesByKey), [day, displayedMinutesByKey]);
  const watchItems = day ? buildWatchItems(day, truckRows, Number(panelData?.alertCount) || 0) : [];

  return (
    <aside
      className={joinClasses([
        "sticky top-0 flex h-full min-h-0 w-full shrink-0 flex-col gap-3 overflow-y-auto rounded-[4px] border border-[rgba(15,92,87,0.11)] bg-surface-results-sidebar p-2.75 shadow-none min-[1281px]:rounded-t-none min-[1281px]:rounded-l-none min-[1281px]:border-y-0 min-[1281px]:border-r-0 max-[1280px]:top-auto max-[1280px]:h-auto max-[1280px]:w-full",
        collapsed ? "min-[1281px]:gap-2 min-[1281px]:px-1.5 min-[1281px]:py-2" : "",
      ])}
      aria-label={panelData?.title || "Panneau droit"}
      data-right-panel-collapsed={collapsed ? "true" : "false"}
      data-right-panel="true"
    >
      {collapseToggle ? (
        <div className={joinClasses(["flex w-full justify-end", collapsed ? "min-[1281px]:justify-center" : ""])}>
          {collapseToggle}
        </div>
      ) : null}

      <PanelBody className={joinClasses(["flex flex-col gap-3", collapsed ? "min-[1281px]:hidden" : ""])}>
        {compact ? <summary className="mobile-right-panel-summary">À surveiller · {watchItems.length}</summary> : null}
        {day ? (
          <>
            <section>
              <SectionTitle>À surveiller</SectionTitle>
              {watchItems.length ? (
                <div className={joinClasses([CARD_CLASS_NAME, "overflow-hidden"])}>
                  {watchItems.map((item) => <WatchRow key={item.key} item={item} />)}
                </div>
              ) : (
                <p className={joinClasses([CARD_CLASS_NAME, "m-0 py-2 text-center text-[11px] text-content-muted"])}>Rien à signaler</p>
              )}
            </section>
            <DaySection day={day} truckRows={truckRows} />
            <DetailTabs day={day} truckRows={truckRows} />
          </>
        ) : (
          <p className={joinClasses([CARD_CLASS_NAME, "m-0 px-2 py-2 text-[11px] text-content-muted"])}>
            Aucun dispatch calculé pour cette journée.
          </p>
        )}
      </PanelBody>
    </aside>
  );
}

export { RightPanel };
export default RightPanel;
